import React from 'react';
import SizeGridInput from './SizeGridInput';
import { SIZES, parseGrid, parseColors, colorTotal, sizeTotalsFromColors, gridTotal } from '../utils/sizeGrid';

// Grade do pedido de roupa pronta, com ou sem divisão por COR.
// value = { size_grid, color_grid }; onChange recebe o par atualizado.
// • Sem cores: tamanhos + quantidade por tamanho (SizeGridInput).
// • Com cores: uma linha por cor (nome, referência/Pantone, qtd por tamanho);
//   a linha de totais por tamanho é calculada.
export default function GarmentGrid({ value, onChange, disabled = false }) {
  const grid = parseGrid(value?.size_grid);
  const colors = parseColors(value?.color_grid);
  const selected = SIZES.filter(s => Object.prototype.hasOwnProperty.call(grid, s));

  const emit = (size_grid, color_grid) => onChange({ size_grid, color_grid });

  function addColor() {
    if (!colors.length) {
      // primeira cor herda o que já estava digitado na grade simples
      const first = { color: '', ref: '', sizes: Object.fromEntries(selected.map(s => [s, grid[s] ?? ''])) };
      const second = { color: '', ref: '', sizes: Object.fromEntries(selected.map(s => [s, ''])) };
      emit(grid, gridTotal(grid) > 0 ? [first, second] : [first]);
      return;
    }
    emit(grid, [...colors, { color: '', ref: '', sizes: Object.fromEntries(selected.map(s => [s, ''])) }]);
  }
  function removeColor(i) {
    const next = colors.filter((_, j) => j !== i);
    // sem cores de novo: a grade simples fica com o que sobrou somado
    if (!next.length) emit(sizeTotalsFromColors(colors, grid), []);
    else emit(grid, next);
  }
  function patchColor(i, changes) {
    emit(grid, colors.map((c, j) => (j === i ? { ...c, ...changes } : c)));
  }
  function setQty(i, size, v) {
    const n = v === '' ? '' : Math.max(0, parseInt(v, 10) || 0);
    patchColor(i, { sizes: { ...colors[i].sizes, [size]: n } });
  }
  function toggleSize(size) {
    if (disabled) return;
    const on = Object.prototype.hasOwnProperty.call(grid, size);
    const nextGrid = {};
    SIZES.forEach(s => {
      if (s === size ? !on : Object.prototype.hasOwnProperty.call(grid, s)) nextGrid[s] = s === size ? '' : grid[s];
    });
    const nextColors = colors.map(c => {
      const sizes = {};
      SIZES.forEach(s => { if (s in nextGrid) sizes[s] = c.sizes[s] ?? ''; });
      return { ...c, sizes };
    });
    emit(nextGrid, nextColors);
  }

  if (!colors.length) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
        <SizeGridInput value={grid} onChange={g => emit(g, [])} disabled={disabled} compact />
        {!disabled && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={addColor}
            title="Dividir a grade por cor" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>＋ Cores</button>
        )}
      </div>
    );
  }

  const totals = sizeTotalsFromColors(colors, grid);
  const grand = Object.values(totals).reduce((s, n) => s + n, 0);
  const th = { fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', padding: '2px 3px', textAlign: 'center' };
  const cellIn = { width: '100%', textAlign: 'center', padding: '4px 2px', fontSize: 12 };
  return (
    <div data-testid="color-grid">
      <table style={{ borderCollapse: 'separate', borderSpacing: '4px 3px', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left', width: 140 }}>Cor</th>
            <th style={{ ...th, textAlign: 'left', width: 96 }} title="Pantone, código do fornecedor…">Ref.</th>
            {SIZES.map(size => {
              const on = Object.prototype.hasOwnProperty.call(grid, size);
              return (
                <th key={size} style={{ ...th, width: 52 }}>
                  <button type="button" onClick={() => toggleSize(size)} disabled={disabled} aria-pressed={on}
                    title={on ? `Retirar ${size} da grade` : `Incluir ${size} na grade`}
                    style={{ width: '100%', padding: '3px 0', fontSize: 11, fontWeight: 700, borderRadius: 6, cursor: disabled ? 'default' : 'pointer',
                      border: `1.5px solid ${on ? 'var(--pantone)' : 'var(--gray-300)'}`,
                      background: on ? 'var(--pantone)' : 'var(--white, #fff)', color: on ? 'white' : 'var(--gray-400)' }}>{size}</button>
                </th>
              );
            })}
            <th style={{ ...th, width: 56 }}>Total</th>
            <th style={{ width: 24 }}></th>
          </tr>
        </thead>
        <tbody>
          {colors.map((c, i) => (
            <tr key={i} data-testid="color-row">
              <td><input value={c.color} disabled={disabled} placeholder="Ex.: Preto" aria-label={`Cor ${i + 1}`}
                onChange={e => patchColor(i, { color: e.target.value })} style={{ width: '100%', padding: '4px 6px', fontSize: 12 }} /></td>
              <td><input value={c.ref} disabled={disabled} placeholder="Pantone / cód." aria-label={`Referência da cor ${i + 1}`}
                onChange={e => patchColor(i, { ref: e.target.value })} style={{ width: '100%', padding: '4px 6px', fontSize: 12 }} /></td>
              {SIZES.map(size => {
                const on = Object.prototype.hasOwnProperty.call(grid, size);
                return (
                  <td key={size}>
                    <input type="number" min="0" step="1" disabled={!on || disabled} placeholder={on ? '0' : '—'}
                      aria-label={`Quantidade ${size} — cor ${i + 1}`} value={on ? (c.sizes[size] ?? '') : ''}
                      onChange={e => setQty(i, size, e.target.value)} style={cellIn} />
                  </td>
                );
              })}
              <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--gray-600)' }}>{colorTotal(c) || '—'}</td>
              <td>
                {!disabled && (
                  <button type="button" onClick={() => removeColor(i)} title="Remover esta cor"
                    style={{ background: 'none', border: 'none', color: 'var(--red,#dc3545)', cursor: 'pointer', fontSize: 13, padding: 0 }}>✕</button>
                )}
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan={2} style={{ fontSize: 10, fontWeight: 700, color: 'var(--pantone)', textTransform: 'uppercase', paddingTop: 4 }}>
              {!disabled && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={addColor} style={{ fontSize: 11, marginRight: 8 }}>＋ Cor</button>
              )}
              Total por tamanho
            </td>
            {SIZES.map(size => (
              <td key={size} style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--pantone)' }}>
                {size in totals ? totals[size] : ''}
              </td>
            ))}
            <td data-testid="size-grid-total" style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: 14, color: 'var(--pantone)' }}>{grand || '—'}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
